/**
 * Die Datenfunktionen aus Classic (`DefaultData`): Name, alte Namen, Parameter je Fassung und
 * eine Kurzbeschreibung (NEX-2074).
 *
 * Steht neben dem Befehlskatalog und importiert nichts: Das Frontend liest die Liste ueber den
 * tsconfig-Pfad, die Engine rechnet mit ihr (`engine/datenfunktionen.ts`).
 */

export interface Datenfunktion {
  name: string;
  /** Alte oder andere Namen derselben Funktion, wie in Classic. */
  aliase: readonly string[];
  /** Parameternamen je Fassung; Classic waehlt die Fassung nach der Zahl der Parameter. */
  fassungen: readonly (readonly string[])[];
  beschreibung: string;
}

/** Vorsilbe einer Datenfunktion in einem Wert, etwa `(fx)=add(1,2)`. */
export const FX_PRAEFIX = '(fx)=';

const DATUM_ABLEITEN = ['format', 'datum', 'monate', 'tage', 'stichtag', 'wochentag'];
const ZWEI_ZAHLEN = ['zahl1', 'zahl2'];

export const DATENFUNKTIONEN: readonly Datenfunktion[] = [
  { name: 'currentTime', aliase: ['time'], fassungen: [['format']], beschreibung: 'Datum und Uhrzeit des Schritts im Muster, z. B. dd.MM.yyyy.' },
  {
    name: 'useDate',
    aliase: [],
    fassungen: [DATUM_ABLEITEN],
    beschreibung: 'Datum ab datum (leer = heute): Monate und Tage verschieben (±n) oder setzen (n), Stichtag, Wochentag.',
  },
  { name: 'berechneDatum', aliase: [], fassungen: [['format', 'datum', 'tage']], beschreibung: 'Alte Form: datum plus ±n Tage oder Stichtag wie EndeMonat.' },
  { name: 'erstelleDatum', aliase: [], fassungen: [['format', 'datum', 'stichtag']], beschreibung: 'Alte Form: datum plus ±n Tage oder Stichtag wie EndeMonat.' },
  {
    name: 'verwendeDatum',
    aliase: [],
    fassungen: [
      ['format', 'datum', 'stichtag', 'wochentag'],
      ['format', 'monate', 'tage', 'stichtag', 'wochentag'],
    ],
    beschreibung: 'Alte Form von useDate, mit 4 oder 5 Parametern.',
  },
  { name: 'diffDays', aliase: ['diffTage'], fassungen: [['format', 'von', 'bis', 'grenze']], beschreibung: 'Tage zwischen von und bis; grenze positive liefert nie weniger als 0.' },
  { name: 'dateTimeFormat', aliase: [], fassungen: [['eingabeformat', 'ausgabeformat', 'datum']], beschreibung: 'Datum von einem Muster in ein anderes umformatieren.' },
  { name: 'formatD', aliase: [], fassungen: [['format', 'datum']], beschreibung: 'Datum der Form dd.MM.yyyy HH:mm:ss ins Muster umformatieren.' },
  {
    name: 'decimalFormat',
    aliase: [],
    fassungen: [['eingabetrenner', 'ausgabeformat', 'ausgabetrenner', 'rundung', 'zahl']],
    beschreibung: 'Zahl im Muster ausgeben, z. B. #,##0.00; Trenner , oder .',
  },
  { name: 'formatN', aliase: [], fassungen: [['format', 'trenner', 'zahl']], beschreibung: 'Alte Form: Zahl im Muster ausgeben.' },
  { name: 'add', aliase: [], fassungen: [ZWEI_ZAHLEN], beschreibung: 'Summe zweier Zahlen, Punkt als Dezimaltrenner.' },
  { name: 'subtract', aliase: ['sub'], fassungen: [ZWEI_ZAHLEN], beschreibung: 'Differenz zweier Zahlen.' },
  { name: 'multiply', aliase: ['mult'], fassungen: [ZWEI_ZAHLEN], beschreibung: 'Produkt zweier Zahlen.' },
  { name: 'divide', aliase: ['div'], fassungen: [ZWEI_ZAHLEN], beschreibung: 'Quotient, so viele Nachkommastellen wie zahl1.' },
  { name: 'textLeft', aliase: ['textLinks'], fassungen: [['muster', 'text']], beschreibung: 'Die ersten n Zeichen, Text vor text=X oder ohne X am Ende (text!=X).' },
  { name: 'textRight', aliase: ['textRechts'], fassungen: [['muster', 'text']], beschreibung: 'Die letzten n Zeichen, Text nach text=X oder ohne X am Anfang (text!=X).' },
  { name: 'match', aliase: [], fassungen: [['regex', 'text']], beschreibung: 'text, wenn er ganz auf den regulären Ausdruck passt, sonst leer.' },
  { name: 'regex', aliase: [], fassungen: [['text']], beschreibung: 'Macht aus Sonderzeichen und Leerraum Platzhalter eines regulären Ausdrucks.' },
  { name: 'replaceAll', aliase: [], fassungen: [['regex', 'ersatz', 'text']], beschreibung: 'Ersetzt jeden Treffer des regulären Ausdrucks.' },
  { name: 'ref', aliase: [], fassungen: [['zeile', 'spaltenname']], beschreibung: 'Wert einer anderen Zeile des Testdatenblatts, Zeile ab 1.' },
];

/** Die Funktion zu einem Namen oder alten Namen; Gross- und Kleinschreibung zaehlt wie in Classic. */
export function findeDatenfunktion(name: string): Datenfunktion | undefined {
  return DATENFUNKTIONEN.find((f) => f.name === name || f.aliase.includes(name));
}

/** Wie die Funktion in einer Vorschlagsliste steht: `name(p1, p2)`, je Fassung eine Zeile. */
export function signaturen(funktion: Datenfunktion, name = funktion.name): string[] {
  return funktion.fassungen.map((p) => `${name}(${p.join(', ')})`);
}

export interface DatenfunktionAufruf {
  name: string;
  /** Spalte des Namens in der Zeile. */
  start: number;
}

/** Jeder `(fx)=name(` einer Zeile, auch eingebettet und geschachtelt. */
export function datenfunktionAufrufe(text: string): DatenfunktionAufruf[] {
  const aufrufe: DatenfunktionAufruf[] = [];
  for (const m of text.matchAll(/\(fx\)=([^(\s"{},]*)\(/gu)) {
    aufrufe.push({ name: m[1], start: m.index + FX_PRAEFIX.length });
  }
  return aufrufe;
}
